import React, { useState, useEffect } from 'react';
import { Check, Sparkles, ShieldCheck, Zap, Loader2, CheckCircle, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchCurrentSubscription();
    }
    
    // Check for success/cancel from Stripe redirect
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (success === 'true' && sessionId) {
      // Wait for user to be loaded, then verify session
      if (user) {
        verifySession(sessionId);
      } else {
        // If user not loaded yet, wait a bit and retry
        const retryVerify = setTimeout(() => {
          if (user) {
            verifySession(sessionId);
          }
        }, 1000);
        return () => clearTimeout(retryVerify);
      }
    } else if (canceled === 'true') {
      toast.warning('Payment process was not completed. Please try again.');
      // Clean URL
      navigate('/pricing', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pricing/plans');
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      // Fallback to empty array if API fails
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await api.get('/users/subscription-status');
      if (response.data.success && response.data.hasActiveSubscription && response.data.planId) {
        setCurrentPlanId(response.data.planId);
      } else {
        setCurrentPlanId(null);
      }
    } catch (error) {
      console.error('Failed to load current subscription:', error);
      setCurrentPlanId(null);
    }
  };

  const handlePlanSelect = async (planId) => {
    if (!user) {
      toast.error('Please login to subscribe to a plan');
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }

    try {
      setProcessingPlan(planId);
      const response = await api.post('/subscriptions/create-checkout-session', { planId });
      
      if (response.data.success) {
        // Redirect to Stripe Checkout for all plans (including $0 free trial)
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          toast.error('Failed to create checkout session');
          setProcessingPlan(null);
        }
      } else {
        toast.error(response.data.message || 'Failed to create checkout session');
        setProcessingPlan(null);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.message || 'Failed to start checkout process');
      setProcessingPlan(null);
    }
  };

  const verifySession = async (sessionId) => {
    try {
      const response = await api.get(`/subscriptions/verify-session?session_id=${sessionId}`);
      
      if (response.data.success) {
        // Payment successful - always show success modal
        toast.success(response.data.message || 'Payment completed successfully!');
        setShowSuccessModal(true);
        
        if (response.data.subscription) {
          // Subscription was found or created - refresh current subscription to update the border
          if (user) {
            await fetchCurrentSubscription();
          }
        } else {
          // Payment completed but subscription not created yet (webhook delayed)
          // Refresh subscription after a delay to check if webhook created it
          setTimeout(async () => {
            if (user) {
              await fetchCurrentSubscription();
            }
          }, 3000);
        }
        // Clean URL
        navigate('/pricing', { replace: true });
      } else {
        // Payment not completed
        toast.warning(response.data.message || 'Payment was not completed. Please try again.');
        // Clean URL
        navigate('/pricing', { replace: true });
      }
    } catch (error) {
      console.error('Error verifying session:', error);
      // Check if payment was actually successful but verification failed
      const errorMessage = error.response?.data?.message || 'Payment verification failed. Please contact support if payment was processed.';
      toast.warning(errorMessage);
      // Still clean URL
      navigate('/pricing', { replace: true });
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Redirect to stories page
    navigate('/stories');
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 lg:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.5em] text-purple-400">Pick your chapter</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4">Select a Subscription Plan</h1>
            <p className="text-lg text-gray-600 mt-3">
              Flexible plans that grow with your child's imagination. Start free, upgrade when you're ready to bring even bigger adventures to life.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-600">No pricing plans available at the moment.</p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {plans.map((plan) => {
                const isCurrentPlan = currentPlanId === plan.id;
                return (
                <div
                  key={plan.id}
                  className={`rounded-[32px] bg-white shadow-xl border ${
                    isCurrentPlan 
                      ? 'border-purple-500 border-2 scale-105' 
                      : plan.isHighlighted 
                        ? 'border-purple-200 scale-105' 
                        : 'border-purple-100'
                  } p-8 flex flex-col gap-4 transition-transform`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="uppercase text-xs tracking-[0.4em] text-purple-400">{plan.name}</p>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">{plan.priceDisplay}</h3>
                      {plan.subtitle && <p className="text-sm text-gray-500">{plan.subtitle}</p>}
                      {plan.trialPeriodDays > 0 && (
                        <span className="inline-flex mt-2 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                          {plan.trialPeriodDays} Day Free Trial
                        </span>
                      )}
                    </div>
                    {(plan.badge || isCurrentPlan) && (
                      <div className="flex items-center gap-2">
                        {plan.badge && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            {plan.badge}
                          </span>
                        )}
                        {isCurrentPlan && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                            Current Plan
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 font-medium">
                    {plan.storiesPerWeek === 'Unlimited'
                      ? 'Unlimited stories per week'
                      : `${plan.storiesPerWeek} stories per week`}
                    {' • '}
                    {plan.storyProfiles} {plan.storyProfiles === 1 ? 'profile' : 'profiles'}
                    {' • '}
                    {plan.storyThemes === 'Unlimited' ? 'Unlimited themes' : `${plan.storyThemes} themes`}
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px] font-medium">
                    <span
                      className={`px-2 py-1 rounded-full ${
                        plan.canSelectLanguage ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {plan.canSelectLanguage ? 'Language selection' : 'Language locked'}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full ${
                        plan.canSelectTone ? 'bg-pink-50 text-pink-600' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {plan.canSelectTone ? 'Tone selection' : 'Tone locked'}
                    </span>
                  </div>
                  <div className="space-y-3 text-sm text-gray-700 flex-1">
                    {plan.features && plan.features.length > 0 ? (
                      plan.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-purple-500" />
                          <span>{feature}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No features listed</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={processingPlan === plan.id || isCurrentPlan}
                    aria-disabled={processingPlan === plan.id || isCurrentPlan}
                    className={`mt-4 w-full py-3 rounded-xl text-white font-semibold shadow-lg bg-gradient-to-r ${plan.color || 'from-purple-500 to-pink-500'} disabled:opacity-50 disabled:cursor-not-allowed transition-opacity`}
                  >
                    {processingPlan === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </span>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      plan.ctaText || 'Select'
                    )}
                  </button>
                </div>
              );
              })}
            </div>
          )}

          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            <div className="p-6 rounded-3xl bg-white border border-purple-100 shadow-lg">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Frequently Asked Questions</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>
                  <strong>Can I cancel anytime?</strong> Absolutely. Your subscription runs on your terms, no long-term contracts.
                </li>
                <li>
                  <strong>Do stories roll over?</strong> Saved stories stay in your library forever, even if you pause a plan.
                </li>
                <li>
                  <strong>Are profiles unlimited?</strong> Plan B and up offer unlimited profiles plus premium voices.
                </li>
              </ul>
            </div>
            <div className="p-6 rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-xl">
              <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Safe & flexible billing
              </h4>
              <p className="text-sm text-white/80">
                Every plan includes privacy-first storage, guardian controls, and secure checkout. Upgrade or downgrade whenever your bedtime ritual changes.
              </p>
            </div>
            <div className="p-6 rounded-3xl bg-white border border-purple-100 shadow-lg">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                Need a team plan?
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Educators and literacy non-profits get bespoke pricing, shared libraries, and training from our story coaches.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-purple-50 text-purple-700 font-semibold"
              >
                Contact sales
                <Zap className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={handleSuccessModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-6">
                Your subscription has been activated successfully. You can now start creating amazing stories for your child!
              </p>
              <button
                onClick={handleSuccessModalClose}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-xl font-semibold shadow-lg transition-all"
              >
                Start Creating Stories
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;


